import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isLoginTransitionActive } from '../utils/loginTransition.jsx';

const safeParse = (value, fallback = null) => {
  if (!value || typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const safeParseNested = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value === 'string') return safeParse(value, fallback);
  return value;
};

const compact = (values = []) => values.filter((value) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0));

const pickFirst = (...values) => compact(values)[0];

const valueFrom = (object, paths = []) => {
  for (const path of paths) {
    const value = String(path || '').split('.').reduce((current, key) => current?.[key], object);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const normalizeRoles = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  if (typeof value === 'string') {
    const parsed = safeParse(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    return value.split(',').map((role) => role.trim()).filter(Boolean);
  }
  return [value].filter(Boolean);
};

const readStoredAuth = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const userData = safeParse(localStorage.getItem('userData'), null);
  const user = safeParse(localStorage.getItem('user'), null);
  const userInfo = safeParse(localStorage.getItem('userInfo'), null);
  const authData = safeParse(localStorage.getItem('authData'), null);
  const persistedRoot = safeParse(localStorage.getItem('persist:root'), {});
  const persistedAuth = safeParseNested(persistedRoot?.auth, {});

  return {
    token: localStorage.getItem('token'),
    authToken: localStorage.getItem('authToken'),
    accessToken: localStorage.getItem('accessToken'),
    accessConfirmationToken: localStorage.getItem('accessConfirmationToken'),
    accessIssuedAt: localStorage.getItem('authSession:accessIssuedAt'),
    email: localStorage.getItem('email'),
    isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
    isLoggedIn: localStorage.getItem('isLoggedIn') === 'true',
    explicitlyLoggedOut: localStorage.getItem('avpsLoggedOut') === 'true',
    tokenExpired: localStorage.getItem('isTokenExpired') === 'true' || localStorage.getItem('tokenExpired') === 'true',
    guestAuthenticated: localStorage.getItem('guestAuthenticated') === 'true',
    roles: normalizeRoles(localStorage.getItem('userRoles')),
    authData,
    persistedAuth,
    userData,
    user,
    userInfo
  };
};

export const readTemplateAuthState = (reduxAuth = {}, storedAuth) => {
  const stored = storedAuth || readStoredAuth();
  const persistedAuth = stored.persistedAuth || {};
  const persistedAuthData = safeParseNested(persistedAuth?.data, {});
  const persistedUserInfo = safeParseNested(persistedAuth?.userInfo, {});
  const persistedUser = safeParseNested(persistedAuth?.user, {});
  const storedAuthData = safeParseNested(stored.authData, {});
  const reduxAuthData = reduxAuth?.data || reduxAuth?.userInfo || reduxAuth?.user || {};
  const authData = {
    ...(storedAuthData || {}),
    ...(persistedUser || {}),
    ...(persistedUserInfo || {}),
    ...(persistedAuthData || {}),
    ...(reduxAuthData || {})
  };
  const nestedData = authData?.data || authData?.payload?.data || {};
  const storedUser = {
    ...(stored.user || {}),
    ...(stored.userInfo || {}),
    ...(stored.userData || {})
  };
  const account = pickFirst(authData?.account, nestedData?.account, storedUser?.account);
  const profile = pickFirst(authData?.profile, nestedData?.profile, storedUser?.profile);
  const userCandidate = {
    ...storedUser,
    ...nestedData,
    ...authData,
    ...(profile || {}),
    account
  };

  const token = pickFirst(
    stored.token,
    stored.authToken,
    stored.accessToken,
    stored.accessConfirmationToken,
    reduxAuth?.token,
    reduxAuth?.accessConfirmationToken,
    persistedAuth?.token,
    persistedAuth?.accessConfirmationToken,
    authData?.token,
    authData?.accessConfirmationToken,
    nestedData?.token,
    nestedData?.accessConfirmationToken
  );
  const email = pickFirst(
    stored.email,
    userCandidate.email,
    userCandidate.userEmail,
    valueFrom(account, ['email', 'contact.email'])
  );
  const roles = normalizeRoles(pickFirst(stored.roles, userCandidate.userRoles, userCandidate.roles, userCandidate.userRole, reduxAuth?.userRoles, persistedAuth?.userRoles));
  const reduxAuthenticated = reduxAuth?.isAuthenticated === true || reduxAuth?.isLoggedIn === true;
  const persistedAuthenticated = persistedAuth?.isAuthenticated === true || persistedAuth?.isAuthenticated === 'true' || persistedAuth?.isLoggedIn === true || persistedAuth?.isLoggedIn === 'true';
  const storageAuthenticated = stored.isAuthenticated || stored.isLoggedIn || stored.guestAuthenticated;
  const explicitlyAuthenticated = reduxAuthenticated || persistedAuthenticated || storageAuthenticated;
  // A completed session is authoritative. A stale role-selection marker must
  // not hide the profile icon after the login flow has already persisted its
  // final authenticated flags.
  const pendingLoginChoice = !explicitlyAuthenticated
    && typeof window !== 'undefined'
    && isLoginTransitionActive(['role-selection', 'employee-choice']);
  const tokenAuthenticated = Boolean(token && !stored.tokenExpired && !pendingLoginChoice);
  const accessSessionAuthenticated = Boolean(stored.accessIssuedAt && !stored.tokenExpired && !pendingLoginChoice);
  // A successful login can update Redux/local auth flags before redux-persist
  // rewrites its previous `loggedOut` snapshot. Only honor those historical
  // markers when there is no completed authenticated session.
  const explicitlyLoggedOut = stored.explicitlyLoggedOut || (
    !explicitlyAuthenticated
    && (persistedAuth?.loggedOut === true || reduxAuth?.loggedOut === true)
  );
  // The regular sign-in flow persists the token/user payload before Redux is
  // hydrated, but some successful login paths do not persist the boolean auth
  // flags. Redux therefore starts with `false` on a full reload even though a
  // valid session is already present. Treat those flags as positive evidence
  // when true, not as a veto against the persisted session evidence below.
  const persistedSessionAuthenticated = explicitlyAuthenticated || tokenAuthenticated || accessSessionAuthenticated;
  const isLoggedIn = !explicitlyLoggedOut
    && !pendingLoginChoice
    && !reduxAuth?.isLoggingOut
    && (reduxAuthenticated || persistedSessionAuthenticated);

  const firstName = pickFirst(userCandidate.firstName, userCandidate.givenName, valueFrom(account, ['firstName', 'contact.firstName']));
  const lastName = pickFirst(userCandidate.lastName, userCandidate.familyName, valueFrom(account, ['lastName', 'contact.lastName']));
  const fullName = compact([firstName, lastName]).join(' ');
  const displayName = pickFirst(userCandidate.fullName, userCandidate.name, userCandidate.userName, fullName, email?.split('@')?.[0], 'Account');
  const initials = compact([firstName?.[0], lastName?.[0]]).join('').toUpperCase() || String(displayName || 'A').slice(0, 1).toUpperCase();
  const avatarUrl = pickFirst(
    valueFrom(userCandidate, ['profilePicture.displayLink', 'profilePicture.storedLink']),
    valueFrom(userCandidate, ['profileImageContent.displayLink', 'profileImageContent.storedLink']),
    valueFrom(userCandidate, ['imageContent.displayLink', 'imageContent.storedLink'])
  );

  return {
    isLoggedIn,
    isGuest: stored.guestAuthenticated,
    displayName,
    email,
    roles,
    initials,
    avatarUrl,
    account
  };
};

export const useTemplateAuthState = () => {
  const reduxAuth = useSelector((state) => state?.auth || {});
  const [storedAuth, setStoredAuth] = useState(() => readStoredAuth());

  useEffect(() => {
    const refresh = () => setStoredAuth(readStoredAuth());
    window.addEventListener('storage', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('avps-auth-updated', refresh);

    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('avps-auth-updated', refresh);
    };
  }, []);

  return useMemo(() => readTemplateAuthState(reduxAuth, storedAuth), [reduxAuth, storedAuth]);
};

export const getTemplateAccountRoute = (domainType = '') => {
  const domain = String(domainType || '').trim().toUpperCase();
  if (domain === 'RETAIL' || domain === 'WAAS') return '/profile';
  return '/user-profile';
};

export const isTemplateAuthRoute = (item = {}) => {
  const label = String(item.label || item.title || '').trim().toLowerCase();
  const route = String(item.route || item.url || item.href || '').trim().toLowerCase();
  return (
    route === '/sign-in'
    || route === '/signin'
    || route === '/login'
    || route === '/sign-up'
    || route === '/signup'
    || route === '/register'
    || ['sign in', 'signin', 'login', 'log in', 'sign up', 'signup', 'register'].includes(label)
  );
};
